/**
 * InputManager - Handles mouse, keyboard, and touch input
 * Tracks mouse/touch position, clicks, and keyboard state for gameplay and menus.
 */

export class InputManager {
  constructor(canvas) {
    this.canvas = canvas;

    // Mouse state
    this.mouseX = 0;
    this.mouseY = 0;
    this.mouseClicked = false;
    this.mouseDown = false;

    // Keyboard state
    this.keys = {};
    this.keysJustPressed = {};

    // Bind event listeners
    this._bindEvents();
  }

  _bindEvents() {
    // Mouse events
    this.canvas.addEventListener('mousemove', (e) => this._onMouseMove(e));
    this.canvas.addEventListener('mousedown', (e) => this._onMouseDown(e));
    this.canvas.addEventListener('mouseup', (e) => this._onMouseUp(e));

    // Touch events (tablet/mobile support)
    this.canvas.addEventListener('touchstart', (e) => this._onTouchStart(e), { passive: false });
    this.canvas.addEventListener('touchmove', (e) => this._onTouchMove(e), { passive: false });
    this.canvas.addEventListener('touchend', (e) => this._onTouchEnd(e));

    // Keyboard events
    window.addEventListener('keydown', (e) => this._onKeyDown(e));
    window.addEventListener('keyup', (e) => this._onKeyUp(e));
  }

  _onMouseMove(e) {
    const rect = this.canvas.getBoundingClientRect();
    this.mouseX = e.clientX - rect.left;
    this.mouseY = e.clientY - rect.top;
  }

  _onMouseDown(e) {
    if (e.button === 0) { // Left click only
      this.mouseDown = true;
      this.mouseClicked = true;
    }
  }

  _onMouseUp(e) {
    if (e.button === 0) {
      this.mouseDown = false;
    }
  }

  _onTouchStart(e) {
    e.preventDefault();
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      const rect = this.canvas.getBoundingClientRect();
      this.mouseX = touch.clientX - rect.left;
      this.mouseY = touch.clientY - rect.top;
      this.mouseDown = true;
      this.mouseClicked = true;
    }
  }

  _onTouchMove(e) {
    e.preventDefault();
    if (e.touches.length > 0) {
      const touch = e.touches[0];
      const rect = this.canvas.getBoundingClientRect();
      this.mouseX = touch.clientX - rect.left;
      this.mouseY = touch.clientY - rect.top;
    }
  }

  _onTouchEnd(e) {
    this.mouseDown = false;
  }

  _onKeyDown(e) {
    if (!this.keys[e.code]) {
      this.keysJustPressed[e.code] = true;
    }
    this.keys[e.code] = true;
  }

  _onKeyUp(e) {
    this.keys[e.code] = false;
  }

  /**
   * Check if a key was just pressed this frame.
   */
  isKeyJustPressed(code) {
    return !!this.keysJustPressed[code];
  }

  /**
   * Check if a key is currently held down.
   */
  isKeyDown(code) {
    return !!this.keys[code];
  }

  /**
   * Check if mouse was clicked this frame (single-fire).
   */
  wasClicked() {
    return this.mouseClicked;
  }

  /**
   * Get current mouse/touch position.
   */
  getPointerPosition() {
    return { x: this.mouseX, y: this.mouseY };
  }

  /**
   * Called at end of each frame to reset per-frame state.
   */
  update() {
    this.mouseClicked = false;
    this.keysJustPressed = {};
  }
}
